import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Heart, CheckCircle, Phone } from 'lucide-react';
import { servicePageContent, companyInfo } from '../data/mock';

const ServicePage = () => {
  const { slug } = useParams();
  const service = servicePageContent[slug];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Helmet>
          <title>Service Not Found | A Gentle Touch HomeCare</title>
          <meta name="description" content="The requested service page was not found." />
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-800 mb-4">Service Not Found</h1>
          <Link to="/" className="text-[#008080] hover:underline">Return to Home</Link>
        </div>
      </div>
    );
  }

  // Generate Service schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.intro,
    "provider": {
      "@type": "HomeHealthCareService",
      "name": "A Gentle Touch HomeCare",
      "url": "https://agentletouchhomecare.com/"
    },
    "areaServed": {
      "@type": "City",
      "name": "Columbus",
      "containedIn": "Ohio"
    },
    "serviceType": "Non Medical Home Care"
  };

  // Generate Breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://agentletouchhomecare.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://agentletouchhomecare.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": service.title,
        "item": `https://agentletouchhomecare.com/services/${slug}`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{service.title} in Columbus, Ohio | A Gentle Touch HomeCare</title>
        <meta name="description" content={`${service.title} by A Gentle Touch HomeCare. Professional non medical home care for seniors in Columbus, Ohio. Call 614-928-4169 for a free consultation.`} />
        <link rel="canonical" href={`https://agentletouchhomecare.com/services/${slug}`} />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Breadcrumb Navigation */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 py-3">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            <li><Link to="/" className="hover:text-[#008080] transition-colors">Home</Link></li>
            <li>/</li>
            <li><Link to="/services" className="hover:text-[#008080] transition-colors">Services</Link></li>
            <li>/</li>
            <li className="text-[#008080] font-medium">{service.title}</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section with Image */}
      <section className="relative">
        <div className="w-full h-[300px] sm:h-[400px] lg:h-[450px] overflow-hidden">
          <img 
            src={service.heroImage} 
            alt={`${service.title} - Non-medical home care service in Columbus Ohio by A Gentle Touch HomeCare`}
            className="w-full h-full object-cover"
            width="1200"
            height="450"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
        </div>
        
        {/* Back Button */}
        <Link 
          to="/services"
          className="absolute top-6 left-6 flex items-center gap-2 text-white bg-black/30 hover:bg-black/50 px-4 py-2 rounded-full transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Services
        </Link>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-semibold text-white drop-shadow-lg">
              {service.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src={companyInfo.logo} 
              alt={`${companyInfo.name} logo`}
              className="h-24 sm:h-28 w-auto"
              width="112"
              height="112"
            />
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
              {service.intro}
            </p>
            {service.subIntro && (
              <p className="text-gray-600 leading-relaxed text-base sm:text-lg mt-4">
                {service.subIntro}
              </p>
            )}
          </div>

          {/* Services List */}
          <div className="bg-gradient-to-br from-[#f8f5f8] to-[#f0f9f9] rounded-2xl p-6 sm:p-8 mb-8">
            <h2 className="text-xl sm:text-2xl font-semibold mb-6" style={{ color: '#008080' }}>
              {service.listTitle}
            </h2>
            <ul className="space-y-4">
              {service.listItems.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#c49bc4] mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Disclosure */}
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 sm:p-6 rounded-r-lg mb-10">
            <p className="text-gray-700 text-sm sm:text-base italic">
              {service.disclosure}
            </p>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-gradient-to-r from-[#008080]/5 to-[#c49bc4]/5 rounded-2xl p-8 sm:p-10">
            <Heart className="w-12 h-12 text-[#c49bc4] mx-auto mb-4" aria-hidden="true" />
            <h2 className="text-xl sm:text-2xl font-semibold mb-3" style={{ color: '#008080' }}>
              Ready to Get Started?
            </h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              Contact us today to learn more about our {service.title.toLowerCase()} and how we can support you or your loved one in Columbus, Ohio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="btn-primary flex items-center justify-center gap-2 px-8 py-3"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                Request Consultation
              </Link>
              <a 
                href="tel:+16149284169"
                className="btn-secondary flex items-center justify-center gap-2 px-8 py-3"
              >
                Call 614-928-4169
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
